import React, { FC, useState } from 'react';
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import type { RunsheetRowNormalized, RunsheetSpeaker } from '@/adapters/runsheet-v2';

/**
 * Editable-row type: the canonical shape plus client-only fields:
 *  - `_key` — React key, preserved across edits/reorders so inputs keep focus.
 *  - `_speakersText` / `_emailsText` — raw text the user is typing in the
 *    Speakers / Emails cells. These are the source of truth for what's on
 *    screen; the canonical `speakers` array is derived from them on every
 *    keystroke. Lets the user type commas mid-word without the chip-like
 *    re-rendering that re-deriving text from `speakers` would cause.
 *
 * Use `withKeys(rows)` when rows enter state, `stripKeys(rows)` when sending
 * them back to the server.
 */
export interface EditableRunsheetRow extends RunsheetRowNormalized {
  _key: string;
  _speakersText: string;
  _emailsText: string;
}

function makeKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback — good enough for a React key.
  return `row_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Parse the two comma-separated text cells into the canonical speakers array.
 * Names split on `,` `;` `&` or the word "and". Emails split on `,` `;`.
 * The i-th name pairs with the i-th email; extras on either side fill in
 * without their counterpart (e.g. 3 names + 2 emails → speaker 3 has no email).
 */
export function parseSpeakersFromText(speakersText: string, emailsText: string): RunsheetSpeaker[] {
  const names = speakersText
    .split(/[;,&]|\s+and\s+/i)
    .map((s) => s.trim())
    .filter(Boolean);
  const emails = emailsText
    .split(/[;,]/)
    .map((s) => s.trim())
    .filter(Boolean);
  const count = Math.max(names.length, emails.length);
  const out: RunsheetSpeaker[] = [];
  for (let i = 0; i < count; i++) {
    const name = names[i] ?? '';
    const email = emails[i];
    if (!name && !email) continue;
    out.push(email ? { name, email } : { name });
  }
  return out;
}

function speakersToText(speakers: RunsheetSpeaker[]): {
  speakersText: string;
  emailsText: string;
} {
  const speakersText = speakers.map((s) => s.name).join(', ');
  const emailsText = speakers.map((s) => s.email ?? '').join(', ');
  // If every email is blank, leave the emails cell empty instead of rendering
  // a string of just commas and spaces.
  const emailsHasAny = speakers.some((s) => !!s.email);
  return {
    speakersText,
    emailsText: emailsHasAny ? emailsText : '',
  };
}

export function withKeys(rows: RunsheetRowNormalized[]): EditableRunsheetRow[] {
  return rows.map((r) => {
    const existing = r as Partial<EditableRunsheetRow>;
    if (
      existing._key &&
      existing._speakersText !== undefined &&
      existing._emailsText !== undefined
    ) {
      return r as EditableRunsheetRow;
    }
    const { speakersText, emailsText } = speakersToText(r.speakers ?? []);
    return {
      ...r,
      speakers: r.speakers ?? [],
      _key: existing._key ?? makeKey(),
      _speakersText: speakersText,
      _emailsText: emailsText,
    };
  });
}

export function stripKeys(rows: EditableRunsheetRow[]): RunsheetRowNormalized[] {
  return rows.map(({ _key: _k, _speakersText: _s, _emailsText: _e, ...rest }) => rest);
}

export function emptyEditableRow(): EditableRunsheetRow {
  return {
    _key: makeKey(),
    _speakersText: '',
    _emailsText: '',
    title: '',
    speakers: [],
  };
}

interface RunsheetRowsTableProps {
  rows: EditableRunsheetRow[];
  onChange(next: EditableRunsheetRow[]): void;
}

/** Where the dragged row will land relative to the row the cursor is over. */
type DropPosition = 'before' | 'after';

interface DragState {
  fromKey: string;
  overKey: string | null;
  position: DropPosition;
}

/** Move an item from `from` to `to`, returning a new array. */
function moveItem<T>(list: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || from >= list.length) return list;
  const next = list.slice();
  const [item] = next.splice(from, 1);
  if (!item) return list;
  const clampedTo = Math.max(0, Math.min(to, next.length));
  next.splice(clampedTo, 0, item);
  return next;
}

const RunsheetRowsTable: FC<RunsheetRowsTableProps> = ({ rows, onChange }) => {
  const [drag, setDrag] = useState<DragState | null>(null);

  const updateRow = (index: number, patch: Partial<EditableRunsheetRow>): void => {
    // Spread preserves `_key`, so the row's React identity survives edits.
    const next = rows.map((r, i) => (i === index ? { ...r, ...patch } : r));
    onChange(next);
  };

  /** Keep `_speakersText` / `_emailsText` as the source of truth, re-derive `speakers`. */
  const updateSpeakersText = (index: number, nextSpeakersText: string): void => {
    const row = rows[index];
    if (!row) return;
    updateRow(index, {
      _speakersText: nextSpeakersText,
      speakers: parseSpeakersFromText(nextSpeakersText, row._emailsText),
    });
  };

  const updateEmailsText = (index: number, nextEmailsText: string): void => {
    const row = rows[index];
    if (!row) return;
    updateRow(index, {
      _emailsText: nextEmailsText,
      speakers: parseSpeakersFromText(row._speakersText, nextEmailsText),
    });
  };

  const removeRow = (index: number): void => {
    onChange(rows.filter((_, i) => i !== index));
  };

  const appendRow = (): void => {
    onChange([...rows, emptyEditableRow()]);
  };

  // ----- DnD handlers -----
  const handleDragStart = (e: React.DragEvent<HTMLElement>, fromKey: string): void => {
    // Required for Firefox to actually fire drag events.
    e.dataTransfer.setData('text/plain', fromKey);
    e.dataTransfer.effectAllowed = 'move';
    setDrag({ fromKey, overKey: null, position: 'before' });
  };

  const handleRowDragOver = (e: React.DragEvent<HTMLTableRowElement>, overKey: string): void => {
    if (!drag) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    // Decide whether the drop goes above or below the row based on where
    // in its height the cursor sits. Splitting at the middle gives the most
    // natural "line shows up above/below" feeling.
    const rect = e.currentTarget.getBoundingClientRect();
    const midpoint = rect.top + rect.height / 2;
    const position: DropPosition = e.clientY < midpoint ? 'before' : 'after';
    if (drag.overKey !== overKey || drag.position !== position) {
      setDrag({ ...drag, overKey, position });
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLElement>): void => {
    e.preventDefault();
    if (!drag) return;
    const { fromKey, overKey, position } = drag;
    setDrag(null);
    if (!overKey || fromKey === overKey) return;

    const fromIndex = rows.findIndex((r) => r._key === fromKey);
    const overIndex = rows.findIndex((r) => r._key === overKey);
    if (fromIndex === -1 || overIndex === -1) return;

    let toIndex = position === 'before' ? overIndex : overIndex + 1;
    // After removing the source row the tail indices shift left by one.
    if (fromIndex < toIndex) toIndex -= 1;

    if (toIndex === fromIndex) return;
    onChange(moveItem(rows, fromIndex, toIndex));
  };

  const handleDragEnd = (): void => {
    setDrag(null);
  };

  return (
    <Box>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" sx={{ minWidth: 920 }}>
          <TableHead>
            <TableRow>
              <TableCell width={32} />
              <TableCell width={36}>#</TableCell>
              <TableCell>Title *</TableCell>
              <TableCell>Speakers *</TableCell>
              <TableCell>Suggested start</TableCell>
              <TableCell>Suggested end</TableCell>
              <TableCell>Speaker emails</TableCell>
              <TableCell width={48} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                  No rows yet. Click &ldquo;Add row&rdquo; to start.
                </TableCell>
              </TableRow>
            )}
            {rows.map((row, index) => {
              const isDragging = drag !== null && drag.fromKey === row._key;
              const isRowOverTarget =
                drag !== null && drag.overKey === row._key && drag.fromKey !== row._key;
              const showTopLine = isRowOverTarget && drag.position === 'before';
              const showBottomLine = isRowOverTarget && drag.position === 'after';

              return (
                <TableRow
                  key={row._key}
                  hover={!drag}
                  onDragOver={(e) => handleRowDragOver(e, row._key)}
                  onDrop={handleDrop}
                  sx={{
                    opacity: isDragging ? 0.4 : 1,
                    transition: 'opacity 120ms ease',
                    boxShadow: showTopLine
                      ? 'inset 0 2px 0 0 #1976d2'
                      : showBottomLine
                      ? 'inset 0 -2px 0 0 #1976d2'
                      : undefined,
                  }}
                >
                  <TableCell
                    // Only the handle is draggable — clicking elsewhere on the
                    // row should still select text inside the inputs.
                    draggable
                    onDragStart={(e) => handleDragStart(e, row._key)}
                    onDragEnd={handleDragEnd}
                    sx={{
                      cursor: 'grab',
                      color: 'text.secondary',
                      userSelect: 'none',
                      textAlign: 'center',
                      '&:active': { cursor: 'grabbing' },
                    }}
                    aria-label="Drag to reorder"
                    title="Drag to reorder"
                  >
                    <DragIndicatorIcon fontSize="small" />
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{index + 1}</TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      value={row.title}
                      onChange={(e) => updateRow(index, { title: e.target.value })}
                      placeholder="Segment title"
                      error={!row.title.trim()}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      value={row._speakersText}
                      onChange={(e) => updateSpeakersText(index, e.target.value)}
                      placeholder="Speaker name"
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      value={row.suggestedStartTime ?? ''}
                      onChange={(e) =>
                        updateRow(index, {
                          suggestedStartTime: e.target.value || undefined,
                        })
                      }
                      placeholder="HH:MM or HH:MM:SS"
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      value={row.suggestedEndTime ?? ''}
                      onChange={(e) =>
                        updateRow(index, {
                          suggestedEndTime: e.target.value || undefined,
                        })
                      }
                      placeholder="HH:MM or HH:MM:SS"
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      value={row._emailsText}
                      onChange={(e) => updateEmailsText(index, e.target.value)}
                      placeholder="name@example.com"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Delete row">
                      <IconButton size="small" onClick={() => removeRow(index)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Tooltip title="Add row">
          <IconButton onClick={appendRow} color="primary" aria-label="Add row">
            <AddIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default RunsheetRowsTable;
